import { useParams, useSearchParams } from 'react-router';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input } from '@leadsnavi/ui-kit';
import { MsgListComponent } from '../../components/msg-list';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Pencil } from 'lucide-react';
import ScrollToBottomIcon from '@/assets/icons/scroll-to-bottom.svg?react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChatInputBarV2 } from '../../components/chat-input-bar';
import {
  ChatEvents,
  chatNotification,
  ChatPageContext,
  createChatPageStore,
  useChatPageStore,
  useSessionSelectedSuppliersStore,
} from '../../store';
import { useConfirm } from '@/components/confirm-dialog';
import useMarketInsightPanelStore from '../../store/market-insight-panel-store';
import { MarketInsightDetailPanel } from '../../components/ai-msg/market-insight/detail-panel';

import { reduce, toArray } from 'lodash-es';
import { useMemoizedFn, useRequest, useUnmount } from 'ahooks';
import { useInsightChipPolling } from '../../hooks/useInsightChipPolling';
import useStore from '@/stores/request-session';
import { readPost, sessionInfoPost, updatePost } from '@/services/swagger/easourcing/SessionService';
// import { ensureLogin } from '@/utils/auth';
import { Ellipse } from '@/common/base/ellipse';

interface BaseChatPageProps {
  sessionId: string;
  messageId?: string;
}

export const BaseChatPage = (props: BaseChatPageProps) => {
  const { sessionId, messageId } = props;

  // Insight Chip 轮询：检测最后一条消息是否含邀约卡片，满足条件则自动轮询并插入 INSIGHT_CHIP sub message
  useInsightChipPolling();

  const [isTitleEditDialogOpen, setIsTitleEditDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const dialogWrapperRef = useRef<HTMLDivElement>(null);
  const fetchMsgList = useChatPageStore(store => store.fetchMsgList);
  const getMsgListByLocate = useChatPageStore(store => store.getMsgListByLocate);
  const getAllNewMsgList = useChatPageStore(store => store.getAllNewMsgList);
  const msgListLength = useChatPageStore(store => store.msgList.length);
  const isLoadingResponse = useChatPageStore(store => store.isLoadingResponse);
  const ask = useChatPageStore(store => store.ask);
  const abort = useChatPageStore(store => store.abort);

  const selectedSuppliers = useChatPageStore(store => {
    return store.selectedSuppliers;
  });
  const clearSelectedSuppliers = useChatPageStore(store => store.clearSelectedSuppliers);
  const deleteSelectedSupplier = useChatPageStore(store => store.deleteSelectedSupplier);

  const { data: sessionInfo, refresh } = useRequest(() => {
    return sessionInfoPost({
      sessionId,
    }).then(res => res.data.sessionDto);
  });
  const updateSessionItem = useStore(store => store.updateSessionItem);
  const loadUnreadStatus = useStore(store => store.loadUnreadStatus);
  const selectedSupplierList = useMemo(() => {
    return toArray(selectedSuppliers);
  }, [selectedSuppliers]);
  const selectedCount = useMemo(() => {
    const countList = selectedSupplierList.map(item => {
      const includeIds = item.selectFilter?.includeIds;
      return includeIds?.length ?? 0;
    });
    // 累计和
    return reduce(countList, (a, b) => a + b, 0);
  }, [selectedSupplierList]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (messageId) {
        getMsgListByLocate(messageId);
      } else {
        fetchMsgList({
          direction: 'oldest',
        });
      }
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [fetchMsgList, getMsgListByLocate, messageId]);
  const { run: saveTitle, loading: saveTitleLoading } = useRequest(
    async () => {
      if (!title) {
        return;
      }
      await updatePost({
        ...sessionInfo,
        updateType: 0,
        sessionId,
        title,
      });
      updateSessionItem(sessionId, { title });
      refresh();
      setIsTitleEditDialogOpen(false);
    },
    {
      throttleWait: 1000,
      manual: true,
    }
  );
  useEffect(() => {
    if (msgListLength > 0) {
      readPost({
        sessionId,
      }).then(() => {
        loadUnreadStatus([sessionId], true);
      });
    }
  }, [msgListLength, isLoadingResponse, sessionId, loadUnreadStatus]);

  const confirm = useConfirm();

  const handleSendInquiry = useMemoizedFn(async () => {
    // 旧的搜索卡片（SEARCH_CARD 类型）不再支持发送邀约，弹出提示弹窗通知用户
    await confirm({
      title: 'Action Unavailable',
      message: "New Inquiries can't be added to past tasks. Please create a new Request to proceed.",
      okText: 'Got it',
      cancelText: 'Cancel',
      showCancel: false,
    });
  });
  const handleSendInquirySuccess = useMemoizedFn((payload: ChatEvents['sendInquirySuccess']) => {
    console.log('handleSendInquirySuccess, payload:', payload);
    const { supplierId } = payload; //这里的 supplierId 就是消息卡片的 subMessageId
    deleteSelectedSupplier(supplierId);
    updateSessionItem(sessionId);
    getAllNewMsgList();
  });
  useEffect(() => {
    const off = chatNotification.on('sendInquirySuccess', payload => handleSendInquirySuccess(payload));
    return () => off();
  }, [handleSendInquirySuccess]);

  const activeMarketInsightStore = useMarketInsightPanelStore(s => s.activeStore);
  const closeMarketInsightPanel = useMarketInsightPanelStore(s => s.closePanel);
  const hideInputTooltip = useChatPageStore(store => store.hideInputTooltip);
  const getListWrapperRef = useChatPageStore(store => store.getListWrapperRef);
  const scrollToBottomForce = useChatPageStore(store => store.scrollToBottomForce);

  // 切换会话时，重置报告详情面板，避免跨会话状态残留
  useEffect(() => {
    return () => {
      useMarketInsightPanelStore.getState().closePanel();
    };
  }, [sessionId]);

  const handleCloseMarketInsightPanel = useCallback(() => {
    closeMarketInsightPanel();
    hideInputTooltip();
  }, [closeMarketInsightPanel, hideInputTooltip]);

  // 距底部距离 > 200px 时显示"回到底部"按钮
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  useEffect(() => {
    const el = getListWrapperRef().current;
    if (!el) return;
    const handleScroll = () => {
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollToBottom(distanceToBottom > 200);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [getListWrapperRef]);

  return (
    <div className="chart-page flex flex-row h-full overflow-hidden relative" ref={dialogWrapperRef}>
      {/* ── 左侧主区域：消息流 + 输入框 ── */}
      <div className="flex flex-col flex-1 py-4 min-w-[330px]">
        {/* 标题部分 */}
        <div className="shrink-0 py-[10px] px-[12px]">
          <div
            className=" group/title inline-flex items-center gap-2  cursor-pointer hover:bg-[#F5F5F5] py-[2px] px-2 rounded-[2px]"
            onClick={() => {
              setIsTitleEditDialogOpen(true);
              setTitle(sessionInfo?.title || '');
            }}
          >
            <Ellipse
              text={sessionInfo?.title || ''}
              className="text-[#262626] group-hover:title:text-[#09090B] max-w-[482px]"
            />
            <Pencil className=" hidden group-hover/title:inline-block" size={16} />
          </div>
        </div>
        {/* 消息列表部分 */}
        <div className="grow overflow-hidden text-base">
          <MsgListComponent dialogWrpperRef={dialogWrapperRef} />
        </div>
        {/* 输入框部分 */}
        <div className="shrink-0 max-w-[800px] w-5/6 mx-auto relative">
          {/* 回到底部按钮：绝对定位，右对齐，浮于输入框上方 12px */}
          <AnimatePresence mode="wait">
            {showScrollToBottom && (
              <motion.div
                key="scroll-to-bottom"
                className="absolute right-0 pointer-events-none"
                style={{ bottom: 'calc(100% + 12px)' }}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.15, ease: 'easeOut' } }}
                exit={{ opacity: 0, y: -6, transition: { duration: 0.12, ease: 'easeIn' } }}
              >
                <button
                  onClick={() => scrollToBottomForce()}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#E5E5E5] cursor-pointer hover:bg-[#F5F5F5] transition-colors pointer-events-auto"
                  style={{ boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.05)' }}
                  aria-label="Scroll to bottom"
                >
                  <ScrollToBottomIcon />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          <ChatInputBarV2
            onSend={ask}
            toggle={!!selectedCount}
            selectedCount={selectedCount}
            onSendInquiry={handleSendInquiry}
            onAbort={abort}
            onClose={clearSelectedSuppliers}
          />
        </div>
      </div>

      {/* ── 右侧详情面板（3列布局第3列）── */}
      <AnimatePresence>
        {activeMarketInsightStore && (
          <motion.div
            key="market-insight-panel"
            className="h-full flex-shrink-0"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 637, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <MarketInsightDetailPanel
              useStore={activeMarketInsightStore}
              onClose={handleCloseMarketInsightPanel}
              className="w-[637px] h-full"
            />
          </motion.div>
        )}
      </AnimatePresence>
      {/* 标题修改弹窗 */}
      <Dialog open={isTitleEditDialogOpen} onOpenChange={setIsTitleEditDialogOpen}>
        <DialogContent
          className="w-120 p-4 pt-5"
          onOpenAutoFocus={e => {
            e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-left leading-[24px] -mt-2">Edit the conversation name</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              placeholder="Please fill Help me find auto parts suppliers in "
              value={title}
              onChange={e => {
                setTitle(e.target.value);
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTitleEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!title} loading={saveTitleLoading} onClick={saveTitle}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BaseChatPageV2 = (props: BaseChatPageProps) => {
  const { sessionId } = props;
  const listWrapperRef = useRef<HTMLDivElement>(null);
  const [store] = useState(() => {
    // 初始化时，从 store 中获取 selectedSuppliers
    const selectedSuppliers = useSessionSelectedSuppliersStore.getState().getSessionSelectedSuppliers(sessionId);
    return createChatPageStore({
      sessionId: props.sessionId,
      selectedSuppliers: selectedSuppliers,
      listWrapperRef,
    });
  });
  useUnmount(() => {
    // 卸载时，将 selectedSuppliers 保存到 store 中，用于后续再次打开时恢复
    const selectedSuppliers = store.getState().selectedSuppliers;
    useSessionSelectedSuppliersStore.getState().setSessionSelectedSuppliers(sessionId, selectedSuppliers);
  });
  return (
    <ChatPageContext.Provider value={store}>
      <BaseChatPage {...props} />
    </ChatPageContext.Provider>
  );
};
export const ChatPage = () => {
  const sessionId = useParams().sessionId;
  const [searchParams] = useSearchParams();
  const messageId = searchParams.get('messageId') || undefined;
  if (!sessionId) {
    return <div>SessionId is required</div>;
  }
  return <BaseChatPageV2 sessionId={sessionId} messageId={messageId} />;
};
